import type { WorkflowConfigurationValue } from '@openphone/dexie-database/dist/shared/workflow'
import { WorkflowSharedTypes } from '@openphone/dexie-database/types'
import isNonNull from '@openphone/lib/isNonNull'
import uuid from '@openphone/lib/uuid'
import { makeAutoObservable, observable, runInAction } from 'mobx'

import {
  getPhoneMenuDestinationByNodeData,
  isPhoneMenuConfigValue,
  isPhoneMenuDigit,
  /* eslint-disable-next-line import-x/no-restricted-paths -- FIXME: UXP-4500 */
} from '@src/app/components/workflow/call-flow/utils'
/* eslint-disable-next-line import-x/no-restricted-paths -- FIXME: UXP-4500 */
import type { StepDefinitionId } from '@src/app/components/workflow/types'
import { DisposeBag } from '@src/lib/DisposeBag/DisposeBag'

import type Service from '.'
import RingOrderFormSchema from './RingOrderFormSchema'
import PersistedCollection from './collections/PersistedCollection'
import { isValidPhoneNumberModelId } from './model'
import type { RawWorkflowDefinition } from './model/workflow/WorkflowDefinitionModel'
import WorkflowDefinitionModel from './model/workflow/WorkflowDefinitionModel'
import type { RawWorkflowTriggerDefinition } from './model/workflow/WorkflowTriggerDefinitionModel'
import WorkflowTriggerDefinitionModel from './model/workflow/WorkflowTriggerDefinitionModel'
import makePersistable from './storage/makePersistable'
import type {
  CreateWorkflowDefinitionParams,
  Validation,
  ValidationErrorCode,
} from './transport/WorkflowClient'
import type { AiTriggerDefinitionUpdateInput } from './transport/ai-client/aiTriggerDefinitionSchema'
import { isAiTriggerDefinitionId } from './transport/ai-client/idSchemas'
import { transformEmptyCriteriaState } from './utils/BranchFilterSerializer'
import {
  getTargetBranchKeyForReuse,
  getUserBranchIndex,
  isUserBranch,
} from './utils/branchUtils'
import { isBranchingEnabled } from './utils/isBranchingEnabled'
import {
  WORKFLOW_DEFINITION_TABLE_NAME,
  type WorkflowDefinitionRepository,
} from './worker/repository/WorkflowDefinitionRepository'
import type { WorkflowTriggerDefinitionRepository } from './worker/repository/WorkflowTriggerDefinitionRepository'
import { WORKFLOW_TRIGGER_DEFINITION_TABLE_NAME } from './worker/repository/WorkflowTriggerDefinitionRepository'

import { getRootLogger } from '@odyssey/logging/logger'

export const generateStepId = () => `WS${uuid()}`.replace(/-/g, '')

type WorkflowBranches = NonNullable<
  WorkflowDefinitionModel['workflowSteps'][string]['branches']
>

function buildConditionalStepBranch({
  branchMultiFilterEnabled,
  targetId,
}: {
  branchMultiFilterEnabled: boolean
  targetId: string | null
}): WorkflowBranches {
  const branches: WorkflowBranches = branchMultiFilterEnabled
    ? [{ key: 'WBbranch0', nextStepId: '' }]
    : []

  if (targetId) {
    branches.push({ key: 'WBbranchElse', nextStepId: targetId })
  }

  return branches
}

function buildDefaultBranch({
  targetId,
  stepDefinitionId,
  defaultBranchKey,
}: {
  targetId: string
  stepDefinitionId: StepDefinitionId
  defaultBranchKey: string | undefined
}): WorkflowBranches {
  // Branches of a goTo step will be created from the side panel, so we don't need to create them here
  if (stepDefinitionId === 'WSDgoTo') {
    return []
  }

  // `defaultBranchKey` should always exist since outputBranches should always
  // have at least one item; guard anyway rather than seed an invalid branch.
  if (!defaultBranchKey) {
    return []
  }

  return [{ key: defaultBranchKey, nextStepId: targetId }]
}

export type WorkflowDefinitionMetadata = Pick<
  RawWorkflowDefinition,
  'emoji' | 'description' | 'name' | 'enabled' | 'updatedAt'
>

/**
 * Parameters for updating a workflow step node
 */
export interface UpdateNodeParams {
  definitionId: string
  stepId: string
  update: Partial<WorkflowDefinitionModel['workflowSteps'][string]>
  skipAddingToHistory?: boolean
}

/**
 * The workflow definition properties that should be updated immediately
 * when changed, as opposed to waiting for the user to click save.
 */
const workflowMetadataKeys: (keyof WorkflowDefinitionMetadata)[] = [
  'emoji',
  'description',
  'name',
  'enabled',
  'updatedAt',
]

/**
 * Thrown when the backend rejects a workflow during validation. Carries the validation
 * `errors` so callers (e.g. the workflow builder) can surface them to the user.
 */
export class WorkflowValidationError extends Error {
  readonly errors: Validation['errors']

  constructor(validation: Validation) {
    super('Workflow failed validation')
    this.name = 'WorkflowValidationError'
    this.errors = validation.errors
  }
}

export default class WorkflowStore {
  private readonly definitionsCollection: PersistedCollection<
    WorkflowDefinitionModel,
    WorkflowDefinitionRepository
  >
  private readonly triggerDefinitionsCollection: PersistedCollection<
    WorkflowTriggerDefinitionModel,
    WorkflowTriggerDefinitionRepository
  >
  // We keep track of the entity ids for which we have fetched definitions
  private readonly fetchedDefinitionsEntityIds = new Set<string>()
  // Tracks in-flight fetchDefinitionsForEntityIds calls by entity id, so that
  // concurrent callers asking for an overlapping set of not-yet-fetched
  // entity ids reuse the same request instead of each starting their own.
  private readonly pendingDefinitionsFetchesByEntityId = new Map<
    string,
    Promise<RawWorkflowDefinition[]>
  >()
  // Same as fetchedDefinitionsEntityIds/pendingDefinitionsFetchesByEntityId
  // above, but for the narrower "enabled only" fetch used by
  // getAllRawRemoteWorkflowDefinitions (e.g. the Sona/AI Inboxes list), which
  // doesn't need an entity's disabled drafts and shouldn't be conflated with
  // having that entity's *full* definition history.
  private readonly fetchedEnabledDefinitionsEntityIds = new Set<string>()
  private readonly pendingEnabledDefinitionsFetchesByEntityId = new Map<
    string,
    Promise<RawWorkflowDefinition[]>
  >()
  // Safety net for the definitions-fetch pagination loop: stops fetching
  // after this many pages so a runaway cursor (or a genuinely huge result
  // set) can't hang the tab.
  private static readonly MAX_DEFINITIONS_FETCH_PAGES = 500
  // Map of definitions that have been modified since they were fetched from the server.
  // This map stores the raw definition before it was modified.
  private readonly definitionsBeforeModificationsById = new Map<
    string,
    RawWorkflowDefinition
  >()
  private readonly historyById = new Map<
    string,
    {
      currentIndex: number | null
      history: RawWorkflowDefinition[]
    }
  >()
  // Keep track of the workflow definitions that have been deleted this session
  private readonly deletedWorkflowDefinitionIds = new Set<string>()

  private readonly disposeBag = new DisposeBag()

  private readonly log = getRootLogger().getChild(['WorkflowStore'])

  constructor(private root: Service) {
    this.definitionsCollection = new PersistedCollection({
      table: root.storage.table(WORKFLOW_DEFINITION_TABLE_NAME),
      classConstructor: (json: RawWorkflowDefinition) =>
        new WorkflowDefinitionModel(json),
    })
    this.triggerDefinitionsCollection = new PersistedCollection({
      table: root.storage.table(WORKFLOW_TRIGGER_DEFINITION_TABLE_NAME),
      classConstructor: (json: RawWorkflowTriggerDefinition) =>
        new WorkflowTriggerDefinitionModel(json),
    })

    makeAutoObservable<this, 'definitionsCollection' | 'triggerDefinitionsCollection'>(
      this,
      {
        definitionsCollection: observable,
        triggerDefinitionsCollection: observable,
      },
    )

    makePersistable(this, 'WorkflowStore', {
      definitionsBeforeModificationsById: root.storage.async(),
      historyById: root.storage.async(),
    })

    this.disposeBag.add(this.subscribeToWebSocket())
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
    this.load()
  }

  getDefinitionById(definitionId: string): WorkflowDefinitionModel | null {
    return this.definitionsCollection.get(definitionId)
  }

  getDefinitionBeforeModificationsById(
    definitionId: string,
  ): WorkflowDefinitionModel | null {
    const rawOriginalDefinition =
      this.definitionsBeforeModificationsById.get(definitionId)

    if (!rawOriginalDefinition) {
      return null
    }

    return new WorkflowDefinitionModel(rawOriginalDefinition)
  }

  markWorkflowAsDeleted(definitionId: string) {
    this.deletedWorkflowDefinitionIds.add(definitionId)
  }

  revertMarkWorkflowAsDeleted(definitionId: string) {
    this.deletedWorkflowDefinitionIds.delete(definitionId)
  }

  wasWorkflowDeletedByUser(definitionId: string): boolean {
    return this.deletedWorkflowDefinitionIds.has(definitionId)
  }

  getWorkflowStepById(
    workflowDefinitionId: string,
    stepId: string,
  ): WorkflowDefinitionModel['workflowSteps'][string] | null {
    const definition = this.getDefinitionById(workflowDefinitionId)

    const workflowStep = definition?.workflowSteps[stepId]

    return workflowStep ?? null
  }

  private getDefinitionsForEntityIds(entityIds: string[]): WorkflowDefinitionModel[] {
    const localDefinitionsForEntityIds = this.definitionsCollection.list.filter(
      (definition) => entityIds.includes(definition.entityId),
    )

    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
    this.fetchDefinitionsForEntityIdsIfNeeded(entityIds, localDefinitionsForEntityIds)

    return localDefinitionsForEntityIds
  }

  getDefinitionsForEntityId(entityId: string): WorkflowDefinitionModel[] {
    return this.getDefinitionsForEntityIds([entityId])
  }

  getNewDefinitionDefaultNameForEntityId(entityId: string): string {
    const definitionsForEntityId = this.getDefinitionsForEntityId(entityId)

    // If no definition name follows the base pattern, then return that
    if (
      !definitionsForEntityId.some((definition) => definition.name === 'New call flow')
    ) {
      return 'New call flow'
    }

    // Otherwise, we will return "New call flow <n>" with the first unused number.

    // Find the next available number by checking what numbers actually exist
    const existingNumbers = definitionsForEntityId
      .map((definition) => {
        const match = definition.name.match(/^New call flow (\d+)$/)
        return match ? parseInt(match[1] ?? '0', 10) : null
      })
      .filter((num): num is number => num !== null)
      .sort((a, b) => a - b)

    // Find the first gap in the sequence, or use the next number after the highest
    let nextNumber = 1
    for (const num of existingNumbers) {
      if (num === nextNumber) {
        nextNumber++
      } else {
        break
      }
    }

    // Return "New call flow <nextNumber>"
    return `New call flow ${nextNumber}`
  }

  getIncomingCallDefinitionsForEntityId(entityId: string): WorkflowDefinitionModel[] {
    return this.getDefinitionsForEntityId(entityId).filter(
      (definition) => definition.isEditableIncomingCallFlow,
    )
  }

  getAllRawRemoteWorkflowDefinitions(entityIds: string[]): RawWorkflowDefinition[] {
    const localDefinitionsForEntityIds = this.definitionsCollection.list.filter(
      (definition) => entityIds.includes(definition.entityId),
    )

    // Callers of this method (e.g. the Sona/AI Inboxes list) only care about
    // enabled definitions, so we only need to fetch those from the server -
    // unlike getDefinitionsForEntityIds, which fetches an entity's full
    // history (enabled and disabled) for flows like the workflow builder.
    void this.fetchEnabledDefinitionsForEntityIdsIfNeeded(entityIds)

    const remoteDefinitionsMap = this.definitionsBeforeModificationsById

    const additionalDefinitions = localDefinitionsForEntityIds
      .filter((definition) => !remoteDefinitionsMap.has(definition.id))
      .map((definition) => definition.serialize())

    return [...remoteDefinitionsMap.values(), ...additionalDefinitions]
  }

  getEnabledIncomingCallDefinitionForEntityId(
    entityId: string,
  ): WorkflowDefinitionModel | null {
    return (
      this.getDefinitionsForEntityId(entityId).find(
        (definition) => definition.isEditableIncomingCallFlow && definition.enabled,
      ) ?? null
    )
  }

  getTriggerDefinitionById(
    triggerDefinitionId: string,
  ): WorkflowTriggerDefinitionModel | null {
    const localTriggerDefinition =
      this.triggerDefinitionsCollection.get(triggerDefinitionId)

    return localTriggerDefinition ?? null
  }

  getHistoryById(definitionId: string) {
    return this.historyById.get(definitionId)
  }

  private updateLocalWorkflow(
    definition: WorkflowDefinitionModel,
    skipAddingToHistory = false,
    skipAugmentingConfiguration = false,
  ) {
    if (!this.definitionsBeforeModificationsById.has(definition.id)) {
      const rawOriginalDefinition = this.definitionsCollection
        .get(definition.id)
        ?.serialize()

      if (!rawOriginalDefinition) {
        return
      }

      this.definitionsBeforeModificationsById.set(
        rawOriginalDefinition.id,
        rawOriginalDefinition,
      )
    }

    const augmentedDefinition = skipAugmentingConfiguration
      ? definition
      : this.augmentDefinitionConfiguration(definition, false)

    this.definitionsCollection.put(augmentedDefinition)

    if (skipAddingToHistory) {
      return
    }

    this.addDefinitionToHistory(augmentedDefinition)
  }

  addDefinitionToHistory(definition: WorkflowDefinitionModel) {
    const currentHistory = this.historyById.get(definition.id)
    const rawOriginalDefinition = this.definitionsBeforeModificationsById.get(
      definition.id,
    )

    if (!rawOriginalDefinition) {
      return
    }

    this.historyById.set(
      definition.id,
      currentHistory
        ? {
            currentIndex: null,
            history: [
              ...currentHistory.history.slice(
                0,
                typeof currentHistory.currentIndex === 'number'
                  ? currentHistory.currentIndex + 1
                  : undefined,
              ),
              definition.serialize(),
            ],
          }
        : {
            currentIndex: null,
            history: [rawOriginalDefinition, definition.serialize()],
          },
    )
  }

  canUndo(definitionId: string): boolean {
    const currentHistory = this.historyById.get(definitionId)

    if (!currentHistory) {
      return false
    }

    const newIndex = this.getPreviousHistoryIndex(definitionId)

    if (newIndex === null) {
      return false
    }

    if (newIndex < 0) {
      return false
    }

    return true
  }

  canRedo(definitionId: string): boolean {
    const currentHistory = this.historyById.get(definitionId)

    if (!currentHistory) {
      return false
    }

    const newIndex = this.getNextHistoryIndex(definitionId)

    if (newIndex === null) {
      return false
    }

    if (newIndex > currentHistory.history.length - 1) {
      return false
    }

    return true
  }

  private getPreviousHistoryIndex(definitionId: string): number | null {
    const currentHistory = this.historyById.get(definitionId)

    if (!currentHistory) {
      return null
    }

    return (currentHistory.currentIndex ?? currentHistory.history.length - 1) - 1
  }

  private getNextHistoryIndex(definitionId: string): number | null {
    const currentHistory = this.historyById.get(definitionId)

    if (!currentHistory) {
      return null
    }

    if (currentHistory.currentIndex === null) {
      return null
    }

    return currentHistory.currentIndex + 1
  }

  undo(definitionId: string) {
    if (!this.canUndo(definitionId)) {
      return
    }

    const newIndex = this.getPreviousHistoryIndex(definitionId)

    this.applyNewHistoryIndex(definitionId, newIndex)
  }

  redo(definitionId: string) {
    if (!this.canRedo(definitionId)) {
      return
    }

    const newIndex = this.getNextHistoryIndex(definitionId)

    this.applyNewHistoryIndex(definitionId, newIndex)
  }

  private applyNewHistoryIndex(definitionId: string, newIndex: number | null) {
    if (newIndex === null) {
      return
    }

    const currentHistory = this.historyById.get(definitionId)

    if (!currentHistory) {
      return
    }

    const rawDefinitionToRestore = currentHistory.history[newIndex]

    if (!rawDefinitionToRestore) {
      return
    }

    // We need to get the latest version of the definition from the collection to make sure the definition we are restoring is still seen
    // as the latest version by the backend
    const latestDefinitionVersion = this.definitionsCollection.get(definitionId)?.version

    const definitionToRestore = new WorkflowDefinitionModel({
      ...rawDefinitionToRestore,
      version: latestDefinitionVersion ?? rawDefinitionToRestore.version,
    })

    this.updateLocalWorkflow(definitionToRestore, true)

    this.historyById.set(definitionId, {
      ...currentHistory,
      currentIndex: newIndex,
    })
  }

  private augmentDefinitionConfiguration(
    definition: WorkflowDefinitionModel,
    preventEmptyPhoneMenuDestinations,
  ) {
    const localDefinition = definition.serialize()

    Object.entries(localDefinition.workflowSteps).forEach(([stepId, step]) => {
      if (step.definitionId !== 'WSDphoneMenu') {
        return
      }

      const builtValue: WorkflowSharedTypes.PhoneMenuConfigValue = {}
      const value = step.configuration?.find(
        (config) => config.variableKey === 'WVphoneMenuOptions',
      )?.value

      step.branches?.forEach((branch) => {
        const isDefaultOption = branch.key === 'WBdefaultOption'
        const phoneMenuConfigKey = isDefaultOption
          ? 'optionDefault'
          : (branch.key.replace(
              'WB',
              '',
            ) as keyof WorkflowSharedTypes.PhoneMenuConfigValue)

        const target = localDefinition.workflowSteps[branch.nextStepId]

        if (!target) {
          return
        }

        const currentValue = isPhoneMenuConfigValue(value) ? value : {}

        const digit = isDefaultOption
          ? 'no-selection'
          : Number(branch.key.replace('WBoption', ''))

        if (!isPhoneMenuDigit(digit)) {
          return
        }

        const destination = getPhoneMenuDestinationByNodeData(
          target.definitionId,
          target.configuration ?? [],
          localDefinition.entityId,
        )

        if (!destination && preventEmptyPhoneMenuDestinations) {
          return
        }

        builtValue[phoneMenuConfigKey] = {
          digit,
          name: currentValue[phoneMenuConfigKey]?.name ?? undefined,
          phrase: currentValue[phoneMenuConfigKey]?.phrase ?? undefined,
          destination,
          stepDefinitionId: target.definitionId,
        }
      })

      step.configuration = [
        ...(step.configuration?.filter(
          (c) =>
            c.variableKey !== 'WVphoneMenuOptions' &&
            c.variableKey !== 'WVphoneMenuDefaultDestination',
        ) ?? []),
        {
          variableKey: 'WVphoneMenuOptions',
          value: builtValue,
        },
        {
          variableKey: 'WVphoneMenuDefaultDestination',
          value: builtValue.optionDefault?.destination ?? '',
        },
      ]

      // sort phone menu steps by key
      localDefinition.workflowSteps[stepId] = {
        ...step,
        branches: step.branches?.sort((a, b) => {
          // Sort branches by key so that the phone menu branches are always ordered by digit (since the digit is part of the key).
          // The default option should always be last. The digit 0 should always be second to last.
          if (a.key === 'WBdefaultOption') {
            return 1
          }

          if (b.key === 'WBdefaultOption') {
            return -1
          }

          if (a.key === 'WBoption0') {
            return 1
          }

          if (b.key === 'WBoption0') {
            return -1
          }

          return a.key < b.key ? -1 : 1
        }),
      }
    })

    return new WorkflowDefinitionModel(localDefinition)
  }

  async enableIncomingCallWorkflow(definitionId: string) {
    const definition = this.definitionsCollection.get(definitionId)

    // Do nothing if the definition is not found, if it's not an incoming call workflow,
    // if it's already enabled, or if it has pending changes
    if (
      !definition?.isEditableIncomingCallFlow ||
      definition.enabled ||
      this.hasChanges(definitionId)
    ) {
      return
    }

    // Get the currently enabled definition so we can optimisitcally disable it
    const enabledIncomingCallDefinition =
      this.getEnabledIncomingCallDefinitionForEntityId(definition.entityId)

    try {
      definition.deserialize({ ...definition.serialize(), enabled: true })
      enabledIncomingCallDefinition?.deserialize({
        ...enabledIncomingCallDefinition.serialize(),
        enabled: false,
      })

      // The web client simply needs to make this call to enable a workflow definition
      // and the backend will ensure that only one incoming call workflow is enabled
      // at a time for a given entityId (i.e. phoneNumberId).
      await this.root.transport.workflow.definitions.enable(definitionId)
    } catch (error) {
      // Revert the changes to both the definitions if something goes wrong.
      // If in the odd chance an error occured but the definitions were still
      // updated on the backend, the client will receive websocket events with
      // the definition updates and the data will correct itself at that point.
      definition.deserialize({ ...definition.serialize(), enabled: false })
      enabledIncomingCallDefinition?.deserialize({
        ...enabledIncomingCallDefinition.serialize(),
        enabled: true,
      })

      throw error
    }
  }

  // Publish the Ai Agent trigger definitions
  async publishAiAgentTriggerDefinitions(definition: WorkflowDefinitionModel) {
    const aiAgentTriggerDefinitionIdsWithStep = Object.values(definition.workflowSteps)
      .map((step) => {
        const triggerDefinitionId = step.configuration?.find(
          (config) => config.variableKey === 'WVagentTriggerDefinitionId',
        )?.value

        // Type guard to ensure it's a valid trigger definition ID
        if (isAiTriggerDefinitionId(triggerDefinitionId)) {
          return [triggerDefinitionId, step] as const
        }
        return null
      })
      .filter((entry) => entry !== null)

    // Update all Ai Agent trigger definitions attached to the workflow
    await Promise.all(
      aiAgentTriggerDefinitionIdsWithStep.map(async ([triggerId, step]) => {
        const localTriggerDefinitions =
          await this.root.ai.automations.localTriggerDefinitions
        const localTriggerDefinition = localTriggerDefinitions.find(
          (t) => t.id === triggerId,
        )
        if (!localTriggerDefinition) {
          return
        }

        const toneValue = step.configuration?.find(
          (config) => config.variableKey === 'WVtone',
        )?.value as 'formal' | 'neutral' | 'casual' | undefined

        const voiceConfigEntry = step.configuration?.find(
          (config) => config.variableKey === 'WVvoiceConfig',
        )?.value
        const voiceConfigValue = WorkflowSharedTypes.isVoiceConfigValue(voiceConfigEntry)
          ? voiceConfigEntry
          : undefined

        const languageValue = localTriggerDefinition.language

        const cleanTriggerDefinition: AiTriggerDefinitionUpdateInput = {
          ...localTriggerDefinition,
          jobs: localTriggerDefinition.jobs,
          ...(toneValue && { tone: toneValue }),
          ...(voiceConfigValue && { voiceConfig: voiceConfigValue }),
          ...(languageValue && { language: languageValue }),
        }

        await this.root.ai.automations.updateTriggerDefinition({
          ...cleanTriggerDefinition,
        })
      }),
    )
  }

  async publishForwardAllCallsWorkflow(definitionId: string) {
    const definition = this.definitionsCollection.get(definitionId)

    if (!definition?.isForwardAllCallsIncomingCallFlow) {
      return
    }

    // We need to store the definitionBeforeModifications object otherwise it will be overwritten by the
    // websocket 'workflow-definition-update' message
    const definitionBeforeModifications =
      this.definitionsBeforeModificationsById.get(definitionId)

    if (!definitionBeforeModifications) {
      return
    }

    const phoneNumber = this.root.organization.phoneNumber.collection.get(
      definition.entityId,
    )
    const forwardStep = Object.values(definition.workflowSteps).find(
      (step) => step.definitionId === 'WSDforwardAllCalls',
    )
    const forwardConfigValue = forwardStep?.configuration?.find(
      (config) => config.variableKey === 'WVforwardeePhoneNumber',
    )?.value

    if (!phoneNumber || typeof forwardConfigValue !== 'string') {
      return
    }

    // Clear the definitionsBeforeModificationsById entry so that the banner disappears
    this.definitionsBeforeModificationsById.delete(definitionId)

    try {
      const validation = await this.validateWorkflow(definitionId)

      if (!validation.valid) {
        throw new WorkflowValidationError(validation)
      }

      const response = await this.root.transport.workflow.definitions.put(
        definition.id,
        definition.serialize(),
      )

      // then store the updated definition in the collection (not necessary since it's already stored, but just in case)
      this.definitionsCollection.put(new WorkflowDefinitionModel(response))
    } catch (error) {
      // if there's an error, store the definitionBeforeModifications object back in the map
      this.definitionsBeforeModificationsById.set(
        definitionId,
        definitionBeforeModifications,
      )

      throw error
    }
  }

  async publishWorkflow(definitionId: string) {
    const definition = this.definitionsCollection.get(definitionId)
    // We need to store the definitionBeforeModifications object otherwise it will be overwritten by the
    // websocket 'workflow-definition-update' message
    const definitionBeforeModifications =
      this.definitionsBeforeModificationsById.get(definitionId)

    if (!definition || !definitionBeforeModifications) {
      return
    }

    // Clear the definitionsBeforeModificationsById entry so that the banner disappears
    this.definitionsBeforeModificationsById.delete(definitionId)

    try {
      const validation = await this.validateWorkflow(definitionId)

      if (!validation.valid) {
        throw new WorkflowValidationError(validation)
      }

      // Publish the Ai Agent trigger definitions before publishing the workflow definition
      await this.publishAiAgentTriggerDefinitions(definition)

      // first create or update any ring orders in this definition
      await this.updateOrCreateRingOrders(definition)

      // then update the definition
      const response = await this.root.transport.workflow.definitions.put(
        definition.id,
        definition.serialize(),
      )

      // then store the updated definition in the collection (not necessary since it's already stored, but just in case)
      this.definitionsCollection.put(new WorkflowDefinitionModel(response))
    } catch (error) {
      // if there's an error, store the definitionBeforeModifications object back in the map
      this.definitionsBeforeModificationsById.set(
        definitionId,
        definitionBeforeModifications,
      )

      throw error
    }
  }

  async validateWorkflow(definitionId: string): Promise<Validation> {
    const definition = this.definitionsCollection.get(definitionId)

    if (!definition) {
      // This should never happen, but let's throw a generic validation error if it does
      return {
        valid: false,
        errors: [
          {
            path: '',
            message: 'Definition not found',
            code: 'UNKNOWN_TYPE',
          },
        ],
      }
    }

    // Validate the workfow against the backend. This way, workflowValidation is initialized to a
    // Validation object.
    const validationDefinition = this.augmentDefinitionConfiguration(
      definition,
      true,
    ).serialize()

    // An unfinished caller-type row's placeholder id survives in the local store (see
    // serializeCallerTypeCriterion) so the row is still there to finish after a
    // persist-and-reparse round trip, but BE's schema validation does not recognize it
    // and would reject it — strip it from this /validate-only copy of the definition.
    validationDefinition.workflowSteps = Object.fromEntries(
      Object.entries(validationDefinition.workflowSteps).map(([stepId, step]) => [
        stepId,
        step.definitionId === 'WSDconditional'
          ? {
              ...step,
              configuration: step.configuration?.map((entry) =>
                /^WVconditional\d+$/.test(entry.variableKey)
                  ? {
                      ...entry,
                      value: transformEmptyCriteriaState(
                        entry.value as WorkflowSharedTypes.ConditionConfigValue,
                      ),
                    }
                  : entry,
              ),
            }
          : step,
      ]),
    )

    const workflowValidation = await this.root.transport.workflow.definitions.validate(
      definitionId,
      validationDefinition,
    )

    // Get all the ringOrderIds that need to be validated from the workflow steps of the definition
    const ringOrderIdsToValidate = Object.values(definition.workflowSteps)
      .map((step) => {
        if (step.definitionId !== 'WSDringUsers') {
          return null
        }

        const ringOrderId = step.configuration?.find(
          (c) => c.variableKey === 'WVringOrderId',
        )?.value

        if (typeof ringOrderId !== 'string') {
          return null
        }

        return ringOrderId
      })
      .filter(isNonNull)

    // For each of them, check if they are valid against the form schema. We use a reduce function here to
    // modify the workflowValidation object that the backend returned.
    const ringOrderValidation: Validation = ringOrderIdsToValidate.reduce<Validation>(
      (validation, ringOrderId) => {
        const ringOrder = this.root.ringOrder.getById(ringOrderId)

        const parse = RingOrderFormSchema.safeParse(ringOrder)

        if (parse.success) {
          return validation
        }

        const workflowStepId = Object.values(definition.workflowSteps).find((step) =>
          step.configuration?.some((config) => config.value === ringOrderId),
        )?.id

        if (!workflowStepId) {
          return validation
        }

        parse.error.issues.forEach((issue) => {
          const value = issue.path.toString()

          const errorCode: ValidationErrorCode = (() => {
            // Note: these values are keys of the various ring order zod schemas
            if (value === 'dialUserId') {
              return 'MISSING_RING_ORDER_SINGLE_USER_ID'
            }

            if (value === 'groups') {
              return 'MISSING_RING_ORDER_CUSTOM_GROUPS'
            }

            // Errors in ring order groups have a value with the format "groups,1,userIds".
            // The comma is included to avoid matching `groups`, which is a different error.
            if (value.startsWith('groups,')) {
              return 'MISSING_RING_ORDER_CUSTOM_GROUP_USER_ID'
            }

            // generic fallback code, shouldn't happen in practice because only the above
            // schema keys should be able to be invalid
            return 'UNKNOWN_TYPE'
          })()

          validation.valid = false
          validation.errors.push({
            path: `workflowSteps/${workflowStepId}/configuration`,
            message: 'Ring order configuration not valid',
            code: errorCode,
            details: value
              ? {
                  code: errorCode,
                  stepId: workflowStepId,
                  property: 'key',
                  value,
                }
              : undefined,
          })
        })

        return validation
      },
      workflowValidation,
    )

    return ringOrderValidation
  }

  discardWorkflow(definitionId: string) {
    const definition = this.definitionsCollection.get(definitionId)

    if (!definition) {
      return
    }

    const rawDefinition = this.definitionsBeforeModificationsById.get(definitionId)

    if (!rawDefinition) {
      return
    }

    this.definitionsCollection.put(new WorkflowDefinitionModel(rawDefinition))
    this.definitionsBeforeModificationsById.delete(definitionId)
    this.historyById.delete(definitionId)

    this.root.ai.automations.discardLocalAiAgentTriggerDefinitions(
      definition.workflowSteps,
    )

    const discardRingOrderConfigChanges = (
      config: WorkflowSharedTypes.WorkflowConfigurationValue,
    ) => {
      if (config.variableKey === 'WVringOrderId' && typeof config.value === 'string') {
        this.root.ringOrder.discardRingOrderChanges(config.value)
      }
    }

    rawDefinition.configuration?.forEach((config) =>
      discardRingOrderConfigChanges(config),
    )
    Object.values(rawDefinition.workflowSteps).forEach((step) => {
      step.configuration?.forEach((config) => discardRingOrderConfigChanges(config))
    })
  }

  fetchTriggerDefinitions() {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
    this.root.transport.workflow.triggerDefinitions.list(1000).then((response) => {
      this.triggerDefinitionsCollection.putBulk(
        response.data.map((item) => new WorkflowTriggerDefinitionModel(item)),
      )
    })
  }

  // Updates metadata of a workflow definition that are eligible for immediate update
  // to reflect in the UI without triggering an update that would register as a change
  // in the history and alert the user to unsaved changes.
  async updateWorkflowMetadata(
    definitionId: string,
    updates: Partial<WorkflowDefinitionMetadata>,
  ) {
    // Don't do anything if there's no keys to update
    if (Object.keys(updates).length === 0) {
      return
    }

    const definitionBeforeModifications =
      this.definitionsBeforeModificationsById.get(definitionId)
    const definition = this.definitionsCollection.get(definitionId)?.serialize()

    // Don't do anything if there's no definition to update
    if (!definition) {
      return
    }

    const hasDifferentValues = Object.keys(updates).some((key) => {
      const initialValue = definition[key as keyof WorkflowDefinitionMetadata]
      const newValue = updates[key as keyof WorkflowDefinitionMetadata]
      return initialValue !== newValue
    })

    // Don't do anything if the updated values aren't different from the current values
    if (!hasDifferentValues) {
      return
    }

    this.definitionsCollection.put(
      new WorkflowDefinitionModel({
        ...definition,
        ...updates,
      }),
    )
    if (definitionBeforeModifications) {
      this.definitionsBeforeModificationsById.set(definitionId, {
        ...definitionBeforeModifications,
        ...updates,
      })
    }

    const definitionHistory = this.historyById.get(definitionId)
    if (definitionHistory) {
      this.historyById.set(definitionId, {
        ...definitionHistory,
        history: definitionHistory.history.map((item) => ({
          ...item,
          ...updates,
        })),
      })
    }

    try {
      // If the user has unsaved step or config changes, we want to ensure that they
      // don't get saved when updating workflow metadata. We do this by passing the
      // definitionBeforeModifications to the server update if it exists. If it doesn't
      // exist, we are safe to pass the definition.
      await this.root.transport.workflow.definitions.put(definitionId, {
        ...(definitionBeforeModifications ?? definition),
        ...updates,
      })
    } catch (error) {
      // revert the optimistically updated definition
      this.definitionsCollection.put(new WorkflowDefinitionModel(definition))
      // revert the optimistically updated definitionBeforeModifications, if it exists
      if (definitionBeforeModifications) {
        this.definitionsBeforeModificationsById.set(
          definitionId,
          definitionBeforeModifications,
        )
      }
      // revert the optimistically updated history items
      if (definitionHistory) {
        this.historyById.set(definitionId, definitionHistory)
      }

      throw error
    }
  }

  addNode({
    definitionId,
    sourceId,
    targetId,
    stepDefinitionId,
    idToReUse,
    incomingBranchKey,
    configuration,
    skipAddingToHistory = false,
  }: {
    definitionId: string
    sourceId: string | null
    targetId: string | null
    stepDefinitionId: StepDefinitionId
    idToReUse?: string
    incomingBranchKey?: string
    configuration?: WorkflowDefinitionModel['configuration']
    skipAddingToHistory?: boolean
  }) {
    const definition = this.definitionsCollection.get(definitionId)?.serialize()

    if (!definition) {
      return
    }

    const branchMultiFilterEnabled = isBranchingEnabled(this.root.flags.flags)

    const stepDefinition =
      this.root.workflowStepDefinition.getStepDefinitionById(stepDefinitionId)

    if (!stepDefinition) {
      return
    }

    const id = idToReUse ?? generateStepId()

    const sourceStep = sourceId ? definition.workflowSteps[sourceId] : null

    // If the source is a step, we need to update the branch that points to the target.
    // If the source is a trigger, we don't need to do anything since there are no branches.
    // If we are providing an idToReUse, it means the branches already exist
    if (sourceStep && !idToReUse) {
      let branchToUpdateIndex =
        sourceStep.branches?.findIndex((branch) => branch.nextStepId === targetId) ?? -1

      // The conditional Branch step pre-creates its user branches (WBbranch{N}) with
      // an empty `nextStepId` before they're connected.
      if (
        branchToUpdateIndex === -1 &&
        sourceStep.definitionId === 'WSDconditional' &&
        incomingBranchKey
      ) {
        branchToUpdateIndex =
          sourceStep.branches?.findIndex(
            (branch) => branch.key === incomingBranchKey && !branch.nextStepId,
          ) ?? -1
      }

      const branchToUpdate =
        sourceStep.branches && branchToUpdateIndex !== -1
          ? sourceStep.branches[branchToUpdateIndex]
          : null

      if (branchToUpdate) {
        branchToUpdate.key = incomingBranchKey ?? branchToUpdate.key
        branchToUpdate.nextStepId = id
      } else {
        const sourceStepDefinition =
          this.root.workflowStepDefinition.getStepDefinitionById(
            sourceStep.definitionId,
            sourceStep.definitionVersion,
          )
        const branchKey = incomingBranchKey
          ? incomingBranchKey
          : sourceStepDefinition?.defaultOrFirstOutputBranch?.key

        if (!branchKey) {
          throw new Error('Branch key not found')
        }

        const newBranch = {
          key: branchKey,
          nextStepId: id,
        }
        const updatedBranches = [...(sourceStep.branches ?? []), newBranch]
        const outputBranches = sourceStepDefinition?.outputBranches

        if (outputBranches?.length) {
          const branchOrderByKey = new Map(
            outputBranches.map(
              (outputBranch) => [outputBranch.key, outputBranch.index] as const,
            ),
          )
          sourceStep.branches = [...updatedBranches].sort((a, b) => {
            const indexA = branchOrderByKey.get(a.key)
            const indexB = branchOrderByKey.get(b.key)
            return (indexA ?? 0) - (indexB ?? 0)
          })
        } else {
          sourceStep.branches = updatedBranches
        }
      }
    }

    const newVoicemailStep: WorkflowDefinitionModel['workflowSteps'][string] = {
      id: generateStepId(),
      definitionId: 'WSDvoicemail',
      branches: [],
      configuration: this.getVoicemailConfiguration({
        phoneNumberId: definition.entityId,
        type:
          // If we're adding a business hours step, the new voicemail step will always be added in the after hours branch
          stepDefinitionId === 'WSDbusinessHours' ||
          // If the incomingBranchKey is WBafterHours, then we can be sure that the new voicemail step will be added
          // in the after hours branch
          incomingBranchKey === 'WBafterHours' ||
          // If none of the above is true, then we need to analyze the source step to determine if it's after business hours or not
          (sourceId && this.isStepAfterBusinessHours(definitionId, sourceId))
            ? 'afterHours'
            : 'duringHours',
      }),
    }

    const newGoToStep: WorkflowDefinitionModel['workflowSteps'][string] | null = {
      id: generateStepId(),
      definitionId: 'WSDgoTo',
      branches: [
        {
          key: 'WBgoTo',
          nextStepId: id,
        },
      ],
    }

    const newVoicemailStepAfterRingUsers: WorkflowDefinitionModel['workflowSteps'][string] =
      {
        ...newVoicemailStep,
        id: generateStepId(),
        configuration:
          stepDefinitionId === 'WSDbusinessHours'
            ? this.getVoicemailConfiguration({
                phoneNumberId: definition.entityId,
                // This step only gets added as a branch of the ringUsers step that gets added in the during hours branch of a new business hours step,
                // so we can be sure that it's always during hours
                type: 'duringHours',
              })
            : this.getVoicemailConfiguration({
                phoneNumberId: definition.entityId,
                type:
                  // If the incomingBranchKey is WBafterHours, then we can be sure that the new voicemail step will be added
                  // in the after hours branch
                  incomingBranchKey === 'WBafterHours' ||
                  // If none of the above is true, then we need to analyze the source step to determine if it's after business hours or not
                  (sourceId && this.isStepAfterBusinessHours(definitionId, sourceId))
                    ? 'afterHours'
                    : 'duringHours',
              }),
      }

    const newRingUsersStep: WorkflowDefinitionModel['workflowSteps'][string] = {
      id: generateStepId(),
      definitionId: 'WSDringUsers',
      branches:
        stepDefinitionId === 'WSDbusinessHours' || stepDefinitionId === 'WSDphoneMenu'
          ? [
              {
                key: 'WBcallMissed',
                nextStepId: newVoicemailStepAfterRingUsers.id,
              },
            ]
          : [],
      configuration: this.getConfiguration(
        'WSDringUsers',
        definition.entityId,
        sourceId,
        definitionId,
      ),
    }

    const newStep: WorkflowDefinitionModel['workflowSteps'][string] = {
      id,
      definitionId: stepDefinitionId,
      configuration: configuration
        ? configuration
        : incomingBranchKey === 'WBduringHours' && stepDefinitionId === 'WSDvoicemail'
          ? this.getVoicemailConfiguration({
              phoneNumberId: definition.entityId,
              type: 'duringHours',
            })
          : incomingBranchKey === 'WBafterHours' && stepDefinitionId === 'WSDvoicemail'
            ? this.getVoicemailConfiguration({
                phoneNumberId: definition.entityId,
                type: 'afterHours',
              })
            : this.getConfiguration(
                stepDefinitionId,
                definition.entityId,
                sourceId,
                definitionId,
              ),
      branches:
        stepDefinitionId === 'WSDvoiceAgent'
          ? [
              {
                key: 'WBfallback',
                nextStepId: targetId ?? newVoicemailStep.id,
              },
            ]
          : stepDefinitionId === 'WSDbusinessHours'
            ? [
                {
                  key: 'WBduringHours',
                  nextStepId: targetId ?? newRingUsersStep.id,
                },
                {
                  key: 'WBafterHours',
                  nextStepId: newVoicemailStep.id,
                },
              ]
            : stepDefinitionId === 'WSDphoneMenu'
              ? [
                  ...(targetId
                    ? [
                        {
                          key: 'WBoption1',
                          nextStepId: targetId,
                        },
                      ]
                    : [
                        {
                          key: 'WBoption1',
                          nextStepId: newRingUsersStep.id,
                        },
                      ]),
                  {
                    key: 'WBdefaultOption',
                    nextStepId: newGoToStep.id,
                  },
                ]
              : stepDefinitionId === 'WSDringUsers'
                ? [
                    {
                      key: 'WBcallMissed',
                      nextStepId: targetId ?? newVoicemailStep.id,
                    },
                  ]
                : stepDefinitionId === 'WSDconditional'
                  ? buildConditionalStepBranch({ branchMultiFilterEnabled, targetId })
                  : !targetId
                    ? []
                    : buildDefaultBranch({
                        targetId,
                        stepDefinitionId,
                        defaultBranchKey: stepDefinition.defaultOrFirstOutputBranch?.key,
                      }),
    }

    const workflowSteps = {
      ...definition.workflowSteps,
      ...(sourceStep ? { [sourceStep.id]: sourceStep } : {}),
      [newStep.id]: newStep,
      ...((stepDefinitionId === 'WSDvoiceAgent' && !targetId) ||
      stepDefinitionId === 'WSDbusinessHours' ||
      (stepDefinitionId === 'WSDringUsers' && !targetId)
        ? { [newVoicemailStep.id]: newVoicemailStep }
        : {}),
      ...(stepDefinitionId === 'WSDbusinessHours' && !targetId
        ? {
            [newRingUsersStep.id]: newRingUsersStep,
            [newVoicemailStepAfterRingUsers.id]: newVoicemailStepAfterRingUsers,
          }
        : {}),
      ...(stepDefinitionId === 'WSDphoneMenu'
        ? {
            [newGoToStep.id]: newGoToStep,
            ...(targetId
              ? {}
              : {
                  [newRingUsersStep.id]: newRingUsersStep,
                  [newVoicemailStepAfterRingUsers.id]: newVoicemailStepAfterRingUsers,
                }),
          }
        : {}),
    }

    const updatedWorkflowDefinition = new WorkflowDefinitionModel({
      ...definition,
      // An undefined sourceStep means this new node was placed
      // immediately after the trigger and is now technically the
      // first step in the definition (because triggers aren't part
      // of definition steps). In this case, update the definition
      // initialStepId to this new step's id.
      // Otherwise, preserve the original value.
      initialStepId: sourceStep ? definition.initialStepId : newStep.id,
      workflowSteps,
    })

    this.updateLocalWorkflow(updatedWorkflowDefinition, skipAddingToHistory)

    return newStep.id
  }

  removeNode({
    definitionId,
    nodeId,
    removeBranchesPointingToNode = true,
    skipAddingToHistory = false,
    skipAugmentingConfiguration = false,
    branchKeyToKeep = null,
  }: {
    definitionId: string
    nodeId: string
    removeBranchesPointingToNode?: boolean
    skipAddingToHistory?: boolean
    skipAugmentingConfiguration?: boolean
    branchKeyToKeep?: string | null
  }) {
    const definition = this.definitionsCollection.get(definitionId)?.serialize()

    if (!definition) {
      return
    }

    const workflowSteps = { ...definition.workflowSteps }
    const workflowStep = workflowSteps[nodeId]

    if (!workflowStep) {
      return
    }

    if (workflowStep.definitionId !== 'WSDgoTo') {
      if (branchKeyToKeep) {
        // Find the branch to keep and update parent branches BEFORE deleting anything
        const branchToKeep = workflowStep.branches?.find(
          (branch) => branch.key === branchKeyToKeep,
        )

        if (!branchToKeep) {
          // Branch key not found, fall back to full deletion
          this.deleteStepAndChildren(definition.id, workflowStep, workflowSteps)
        } else {
          // Selective deletion: keep one branch, delete others
          // Update parent branches to point to kept branch's target FIRST
          // This includes both regular steps AND the trigger (via initialStepId)

          // Check if this step is the initial step (directly after trigger)
          if (definition.initialStepId === nodeId) {
            definition.initialStepId = branchToKeep.nextStepId
          }

          // Check regular workflow steps - update ALL steps pointing to nodeId
          Object.values(workflowSteps).forEach((step) => {
            if (step.branches) {
              step.branches.forEach((branch) => {
                if (branch.nextStepId === nodeId) {
                  branch.nextStepId = branchToKeep.nextStepId
                }
              })
            }
          })

          // First, identify all steps in the kept branch (we must never delete these)
          const keptStep = workflowSteps[branchToKeep.nextStepId]
          const stepsToKeep = new Set<string>()
          if (keptStep) {
            stepsToKeep.add(keptStep.id)
            // Only follow structural children, not Go-To references.
            // If keptStep is a Go-To, its destination is a reference, not a structural child,
            // so we should not protect it from deletion.
            if (keptStep.definitionId !== 'WSDgoTo') {
              this.getAllChildrenSteps(definition.id, keptStep).forEach((step) => {
                stepsToKeep.add(step.id)
              })
            }
          }

          // Now delete children of branches we don't want to keep
          const branchesToDelete =
            workflowStep.branches?.filter((branch) => branch.key !== branchKeyToKeep) ??
            []

          branchesToDelete.forEach((branch) => {
            const nextStep = workflowSteps[branch.nextStepId]
            if (!nextStep) {
              return
            }

            // Get all descendants of this branch
            // For go-to steps, don't follow their destination - it's a reference, not a child
            const childrenToDelete =
              nextStep.definitionId === 'WSDgoTo'
                ? []
                : this.getAllChildrenSteps(definition.id, nextStep)

            // Delete all descendants (but only if they're not in the kept branch)
            childrenToDelete.forEach((step) => {
              if (!stepsToKeep.has(step.id)) {
                delete workflowSteps[step.id]
              }
            })

            // Also delete the immediate child (but only if not in kept branch)
            if (!stepsToKeep.has(nextStep.id)) {
              delete workflowSteps[nextStep.id]
            }
          })

          // Clean up any orphaned branches pointing to deleted steps
          Object.values(workflowSteps).forEach((step) => {
            if (step.id === nodeId) {
              // Don't clean up the step we're about to delete
              return
            }

            step.branches = step.branches?.filter((branch) => {
              // Prevents orphaning of never-connected branches (e.g. an unconfigured "If..." condition)
              if (!branch.nextStepId) {
                return true
              }

              return !!workflowSteps[branch.nextStepId]
            })
          })
        }
      } else {
        // Original logic: delete all children
        this.deleteStepAndChildren(definition.id, workflowStep, workflowSteps)
      }
    }

    delete workflowSteps[nodeId]

    if (removeBranchesPointingToNode) {
      // Unlike the cleanup above, this filters by equality against the specific
      // deleted nodeId rather than by target existence, so a never-connected
      // branch (nextStepId === '') can never match and doesn't need the same
      // empty-string guard.
      Object.values(workflowSteps).forEach((step) => {
        step.branches = step.branches?.filter((branch) => branch.nextStepId !== nodeId)
      })
    }

    const updatedWorkflowDefinition = new WorkflowDefinitionModel({
      ...definition,
      workflowSteps,
    })

    this.updateLocalWorkflow(
      updatedWorkflowDefinition,
      skipAddingToHistory,
      skipAugmentingConfiguration,
    )
  }

  replaceNode({
    definitionId,
    stepId,
    newStepDefinitionId,
    branchKeyToKeep = null,
  }: {
    definitionId: string
    stepId: string
    newStepDefinitionId: StepDefinitionId
    branchKeyToKeep?: string | null
  }) {
    const definition = this.definitionsCollection.get(definitionId)?.serialize()

    if (!definition) {
      return
    }

    const source = this.getSources(definitionId, stepId).filter(
      (source) => source.definitionId !== 'WSDgoTo',
    )[0]

    const incomingBranchKey = source?.branches?.find(
      (branch) => branch.nextStepId === stepId,
    )?.key

    // Capture the stepId of the kept branch (if any) before removal
    const keptBranchStepId = (() => {
      if (!branchKeyToKeep) {
        return null
      }

      const oldStep = definition.workflowSteps[stepId]
      const selectedBranch = oldStep?.branches?.find(
        (branch) => branch.key === branchKeyToKeep,
      )
      return selectedBranch?.nextStepId ?? null
    })()

    // Remove node with branch preservation if branchKeyToKeep provided
    this.removeNode({
      definitionId,
      nodeId: stepId,
      removeBranchesPointingToNode: false,
      skipAddingToHistory: true,
      skipAugmentingConfiguration: true,
      branchKeyToKeep,
    })

    // Add new node with same ID
    // If preserving a branch, pass keptBranchStepId so addNode creates the correct connection
    // Skip adding to history here - we'll add the final state after all rewiring is complete
    this.addNode({
      idToReUse: stepId,
      definitionId,
      sourceId: source?.id ?? null,
      targetId: keptBranchStepId,
      stepDefinitionId: newStepDefinitionId,
      incomingBranchKey,
      skipAddingToHistory: true,
    })

    // Get the current definition state after addNode
    const updatedDefinition = this.definitionsCollection.get(definitionId)?.serialize()

    if (!updatedDefinition) {
      return
    }

    // If we preserved a branch, manually rewire the connections
    if (keptBranchStepId && branchKeyToKeep) {
      // Step 1: Restore parent → new step connection
      // After removeNode with branchKeyToKeep, the parent points to keptBranchStepId
      // We need to rewire it to point to our new step instead
      if (source) {
        const sourceStep = updatedDefinition.workflowSteps[source.id]
        if (sourceStep?.branches) {
          const branchToUpdate = sourceStep.branches.find(
            (branch) => branch.nextStepId === keptBranchStepId,
          )
          if (branchToUpdate) {
            branchToUpdate.nextStepId = stepId
          }
        }
      } else if (updatedDefinition.initialStepId === keptBranchStepId) {
        // If there's no source step, the trigger might point to keptBranchStepId
        updatedDefinition.initialStepId = stepId
      }

      // Step 2: Wire new step's target branch → preserved subtree
      // Determine which branch of the new step should point to the preserved target
      const newStep = updatedDefinition.workflowSteps[stepId]
      const targetBranchKey = getTargetBranchKeyForReuse(newStepDefinitionId)

      if (targetBranchKey && newStep) {
        // Ensure branches array exists
        if (!newStep.branches) {
          newStep.branches = []
        }

        // Find or create the target branch
        const branchToUpdate = newStep.branches.find(
          (branch) => branch.key === targetBranchKey,
        )

        if (branchToUpdate) {
          // Branch exists, update its target
          branchToUpdate.nextStepId = keptBranchStepId
        } else {
          // Branch doesn't exist, create it
          newStep.branches.push({
            key: targetBranchKey,
            nextStepId: keptBranchStepId,
          })
        }
      }
    }

    // Update the definition and add the final state to history (after all rewiring is complete)
    this.updateLocalWorkflow(new WorkflowDefinitionModel(updatedDefinition))
  }

  updateNode({
    definitionId,
    stepId,
    update,
    skipAddingToHistory = false,
  }: UpdateNodeParams) {
    const definition = this.definitionsCollection.get(definitionId)?.serialize()

    if (!definition) {
      return
    }

    const workflowSteps = { ...definition.workflowSteps }

    const updatedStep = {
      ...workflowSteps[stepId],
      ...update,
    }

    // @ts-expect-error unchecked index access
    workflowSteps[stepId] = updatedStep

    const updatedWorkflowDefinition = new WorkflowDefinitionModel({
      ...definition,
      workflowSteps,
    })

    this.updateLocalWorkflow(updatedWorkflowDefinition, skipAddingToHistory)
  }

  addBranchToStep({
    definitionId,
    stepId,
  }: {
    definitionId: string
    stepId: string
  }): string | null {
    const MAX_BRANCHES = 20
    const step = this.getWorkflowStepById(definitionId, stepId)

    if (step?.definitionId !== 'WSDconditional' || !step.branches) {
      return null
    }
    // Allocate the next index above EVERY existing user branch
    // The highest free index is scanned for instead of trusting array order (WBbranch0..N then WBbranchElse)
    // Deleting and then re-adding branches can leave the configuration non-canonical
    // e.g. removeBranchFromStep's shared-subtree prune drops a branch edge without removing its WVconditional
    // entry, orphaning that index. An order-dependent scan would then reuse the
    // index, producing a duplicate branch that overlaps WBbranchElse.
    let maxIndex = -1
    for (const branch of step.branches) {
      const branchIndex = getUserBranchIndex(branch.key)
      if (branchIndex !== null) {
        maxIndex = Math.max(maxIndex, branchIndex)
      }
    }
    for (const entry of step.configuration ?? []) {
      const match = /^WVconditional(\d+)$/.exec(entry.variableKey)
      if (match?.[1] !== undefined) {
        maxIndex = Math.max(maxIndex, Number(match[1]))
      }
    }
    const freeIndex = maxIndex + 1

    if (freeIndex >= MAX_BRANCHES) {
      return null
    }

    const newBranchKey = `WBbranch${freeIndex}`
    const newBranch = { key: newBranchKey, nextStepId: '' }

    const existingBranches = step.branches ?? []
    const elseIndex = existingBranches.findIndex(
      (branch) => branch.key === 'WBbranchElse',
    )
    const branches =
      elseIndex === -1
        ? [...existingBranches, newBranch]
        : [
            ...existingBranches.slice(0, elseIndex),
            newBranch,
            ...existingBranches.slice(elseIndex),
          ]

    const configuration: WorkflowSharedTypes.WorkflowConfigurationValue[] = [
      ...(step.configuration ?? []),
      {
        variableKey: `WVconditional${freeIndex}`,
        value: { type: 'and', conditions: [] },
      },
    ]

    this.updateNode({ definitionId, stepId, update: { branches, configuration } })

    return newBranchKey
  }

  removeBranchFromStep({
    definitionId,
    stepId,
    branchKey,
  }: {
    definitionId: string
    stepId: string
    branchKey: string
  }): boolean {
    const definition = this.definitionsCollection.get(definitionId)?.serialize()

    if (!definition) {
      return false
    }

    const step = definition.workflowSteps[stepId]

    if (step?.definitionId !== 'WSDconditional' || !step.branches) {
      return false
    }

    if (!isUserBranch(branchKey)) {
      return false
    }

    const branch = step.branches.find((entry) => entry.key === branchKey)
    if (!branch) {
      return false
    }

    const workflowSteps = { ...definition.workflowSteps }

    // Delete the subtree this branch routes to to avoid UNREACHABLE_STEP exception
    // getAllChildrenSteps stops at goTo references, so shared go-to destinations are left intact.
    const deletedStepIds = new Set<string>()
    const targetStep = branch.nextStepId ? workflowSteps[branch.nextStepId] : undefined
    if (targetStep) {
      const subtree = [targetStep, ...this.getAllChildrenSteps(definitionId, targetStep)]
      subtree.forEach((descendant) => {
        deletedStepIds.add(descendant.id)
        delete workflowSteps[descendant.id]
      })
    }

    const parentStep = workflowSteps[stepId]
    if (parentStep) {
      const { branches, configuration } = this.reorderRemainingBranches(
        parentStep,
        branchKey,
      )
      parentStep.branches = branches
      parentStep.configuration = configuration
    }

    // Prune any other branch edges that pointed into the deleted subtree (e.g.
    // a step shared by more than one branch).
    if (deletedStepIds.size > 0) {
      Object.values(workflowSteps).forEach((entry) => {
        entry.branches = entry.branches?.filter((b) => !deletedStepIds.has(b.nextStepId))
      })
    }

    const updatedWorkflowDefinition = new WorkflowDefinitionModel({
      ...definition,
      workflowSteps,
    })

    this.updateLocalWorkflow(updatedWorkflowDefinition)

    return true
  }

  private isReorderValid(
    fromIndex: number,
    toIndex: number,
    orderedUserIndices: number[],
  ) {
    return (
      fromIndex !== toIndex &&
      fromIndex >= 0 &&
      toIndex >= 0 &&
      fromIndex < orderedUserIndices.length &&
      toIndex < orderedUserIndices.length
    )
  }

  private getNewBranchIndexOrder(
    fromIndex: number,
    toIndex: number,
    currentOrder: number[],
  ) {
    const movedIndices = [...currentOrder]
    const [moved] = movedIndices.splice(fromIndex, 1)
    if (moved === undefined) {
      return false
    }
    movedIndices.splice(toIndex, 0, moved)
    return movedIndices
  }

  private getBranchIndexRemap(newIndexOrder: number[]) {
    const branchIndexRemap = new Map<number, number>()
    newIndexOrder.forEach((oldIndex, newIndex) => {
      branchIndexRemap.set(oldIndex, newIndex)
    })
    return branchIndexRemap
  }

  private getReorderedBranches(
    branchIndexRemap: Map<number, number>,
    branches: {
      key: string
      nextStepId: string
      name?: string
    }[],
  ) {
    const userBranches = branches
      .flatMap((branch) => {
        const index = getUserBranchIndex(branch.key)
        if (index === null) {
          return []
        }
        const newIndex = branchIndexRemap.get(index)
        return newIndex === undefined
          ? []
          : [{ branch: { ...branch, key: `WBbranch${newIndex}` }, newIndex }]
      })
      .sort((a, b) => a.newIndex - b.newIndex)
      .map(({ branch }) => branch)
    const otherBranches = branches.filter(
      (branch) => getUserBranchIndex(branch.key) === null,
    )

    return [...userBranches, ...otherBranches]
  }

  /**
   * Updates configuration to put respective config value to the branch new index
   * Each entry is rewritten from its own old key, so swaps don't collide.
   **/
  private updateConfigurationForReorderedBranches(
    configuration: WorkflowConfigurationValue[] | undefined,
    branchIndexRemap: Map<number, number>,
  ) {
    if (!configuration) {
      return
    }
    return (configuration ?? []).map((entry) => {
      const match = /^WVconditional(\d+)$/.exec(entry.variableKey)
      if (!match) {
        return entry
      }
      const newIndex = branchIndexRemap.get(Number(match[1]))
      return newIndex === undefined
        ? entry
        : { ...entry, variableKey: `WVconditional${newIndex}` }
    })
  }

  /**
   * Re-orders the user-defined criteria branches of the Branch step triggered by drag
   *
   * Returns `false` (no-op) for non-conditional steps, out-of-range indices, or
   * when `fromIndex === toIndex`.
   */
  reOrderBranches({
    definitionId,
    stepId,
    fromIndex,
    toIndex,
  }: {
    definitionId: string
    stepId: string
    fromIndex: number
    toIndex: number
  }): boolean {
    const definition = this.definitionsCollection.get(definitionId)?.serialize()

    if (!definition) {
      return false
    }

    const step = definition.workflowSteps[stepId]

    if (step?.definitionId !== 'WSDconditional' || !step.branches) {
      return false
    }

    const currentOrderedUserIndices = step.branches
      .flatMap((branch) => {
        const index = getUserBranchIndex(branch.key)
        return index === null ? [] : [index]
      })
      .sort((a, b) => a - b)

    if (!this.isReorderValid(fromIndex, toIndex, currentOrderedUserIndices)) {
      return false
    }

    const newOrder = this.getNewBranchIndexOrder(
      fromIndex,
      toIndex,
      currentOrderedUserIndices,
    )
    if (!newOrder) {
      return false
    }

    const branchIndexRemap = this.getBranchIndexRemap(newOrder)

    const reorderedBranches = this.getReorderedBranches(branchIndexRemap, step.branches)

    const configuration = this.updateConfigurationForReorderedBranches(
      step.configuration,
      branchIndexRemap,
    )

    if (!configuration) {
      return false
    }

    this.updateNode({
      definitionId,
      stepId,
      update: { branches: reorderedBranches, configuration },
    })

    return true
  }

  /**
   * Drops `removedBranchKey` and compacts the remaining user branches to
   * contiguous indices to make sure `addBranchToStep` always appends at the end
   * instead of back-filling a freed gap at an unpredictable position.
   * `WBbranchElse` (and any other non-user branch / non-branch config entry) is
   * preserved untouched and in place.
   */
  private reorderRemainingBranches(
    step: WorkflowDefinitionModel['workflowSteps'][string],
    removedBranchKey: string,
  ): Pick<
    WorkflowDefinitionModel['workflowSteps'][string],
    'branches' | 'configuration'
  > {
    const oldToNewIndex = new Map<number, number>()
    ;(step.branches ?? [])
      .flatMap((entry) => {
        if (entry.key === removedBranchKey) {
          return []
        }
        const index = getUserBranchIndex(entry.key)
        return index === null ? [] : [index]
      })
      .sort((a, b) => a - b)
      .forEach((oldIndex, newIndex) => {
        oldToNewIndex.set(oldIndex, newIndex)
      })

    const branches = (step.branches ?? []).flatMap((entry) => {
      if (entry.key === removedBranchKey) {
        return []
      }
      const index = getUserBranchIndex(entry.key)
      if (index === null) {
        return [entry]
      }
      const newIndex = oldToNewIndex.get(index)
      return newIndex === undefined ? [] : [{ ...entry, key: `WBbranch${newIndex}` }]
    })

    const configuration = (step.configuration ?? []).flatMap((entry) => {
      const match = /^WVconditional(\d+)$/.exec(entry.variableKey)
      if (!match) {
        return [entry]
      }
      const newIndex = oldToNewIndex.get(Number(match[1]))
      return newIndex === undefined
        ? []
        : [{ ...entry, variableKey: `WVconditional${newIndex}` }]
    })

    return { branches, configuration }
  }

  hasChanges(definitionId: string) {
    return this.definitionsBeforeModificationsById.has(definitionId)
  }

  isStepAfterBusinessHours(definitionId: string, stepId: string): boolean {
    const previousSteps = this.getPreviousSteps(definitionId, stepId, false)
    const isAfterBusinessHours = previousSteps.some((node) =>
      this.getDefinitionById(definitionId)?.workflowSteps[node.id]?.branches?.some(
        (branch) =>
          branch.key === 'WBafterHours' &&
          (previousSteps.some((n) => n.id === branch.nextStepId) ||
            stepId === branch.nextStepId),
      ),
    )

    return isAfterBusinessHours
  }

  private getConfiguration(
    stepDefinitionId: StepDefinitionId,
    phoneNumberId: string,
    sourceWorkflowStepId: string | null,
    definitionId: string | null,
  ): WorkflowDefinitionModel['configuration'] {
    if (stepDefinitionId === 'WSDringUsers') {
      const newRingOrder = this.root.ringOrder.addRingOrderToCollection(phoneNumberId)

      if (!newRingOrder) {
        throw new Error('Failed to create ring order')
      }

      return [
        {
          variableKey: 'WVringOrderId',
          value: newRingOrder.id,
        },
      ]
    }

    if (stepDefinitionId === 'WSDconditional') {
      return [
        {
          variableKey: 'WVconditional0',
          value: {
            type: 'and',
            conditions: [],
          },
        },
      ]
    }

    if (stepDefinitionId === 'WSDvoicemail' && definitionId) {
      return sourceWorkflowStepId
        ? this.getVoicemailConfiguration({
            definitionId,
            workflowStepId: sourceWorkflowStepId,
            phoneNumberId,
          })
        : this.getVoicemailConfiguration({
            phoneNumberId,
            type: 'duringHours',
          })
    }

    return []
  }

  private getVoicemailConfiguration(
    args: {
      phoneNumberId: string
    } & (
      | {
          definitionId: string
          workflowStepId: string
        }
      | { type: 'afterHours' | 'duringHours' }
    ),
  ) {
    const phoneNumber = this.root.organization.phoneNumber.collection.get(
      args.phoneNumberId,
    )

    if ('definitionId' in args && 'workflowStepId' in args) {
      return [
        {
          value: {
            audioUrl:
              (this.isStepAfterBusinessHours(args.definitionId, args.workflowStepId)
                ? phoneNumber?.awayVoicemailUrl
                : phoneNumber?.voicemailUrl) ?? '',
            textToSpeechText: '',
          },
          variableKey: 'WVvoicemail',
        },
      ]
    }

    return [
      {
        value: {
          audioUrl:
            (args.type === 'duringHours'
              ? phoneNumber?.voicemailUrl
              : phoneNumber?.awayVoicemailUrl) ?? '',
          textToSpeechText: '',
        },
        variableKey: 'WVvoicemail',
      },
    ]
  }

  async fetchDefinitionsForEntityIdsIfNeeded(
    entityIds: string[],
    localDefinitionsForEntityIds: WorkflowDefinitionModel[],
  ) {
    const entityIdsToFetch = entityIds.filter(
      (entityId) => !this.fetchedDefinitionsEntityIds.has(entityId),
    )
    if (entityIdsToFetch.length === 0) {
      return
    }

    const { remoteDefinitions, successfullyFetchedEntityIds } =
      await this.fetchDefinitionsForEntityIdsWithDedup(
        entityIdsToFetch,
        this.pendingDefinitionsFetchesByEntityId,
        (ids) => this.fetchDefinitionsForEntityIds(ids),
      )

    runInAction(() => {
      successfullyFetchedEntityIds.forEach((entityId) => {
        this.fetchedDefinitionsEntityIds.add(entityId)
      })
    })

    // Remove the local entities that have been deleted in the remote for
    // the entity ids that we just successfully fetched
    localDefinitionsForEntityIds.forEach((localDefinition) => {
      if (!successfullyFetchedEntityIds.includes(localDefinition.entityId)) {
        return
      }

      const remoteDefinition = remoteDefinitions.find(
        (remoteDefinition) => remoteDefinition.id === localDefinition.id,
      )

      if (!remoteDefinition) {
        this.definitionsCollection.delete(localDefinition.id)
      }
    })

    remoteDefinitions.forEach((remoteDefinition) => {
      this.handleRemoteDefinitionUpdate(remoteDefinition)
    })
  }

  // Narrower counterpart to fetchDefinitionsForEntityIdsIfNeeded: only fetches
  // enabled definitions, for callers (like getAllRawRemoteWorkflowDefinitions)
  // that don't need an entity's disabled drafts. Entity ids that already have
  // their *full* history fetched are skipped, since that's a superset of this
  // data. There's no remote-deletion reconciliation here - the fetched data is
  // intentionally partial, so "missing from this response" doesn't mean
  // "deleted remotely", it may just mean "disabled".
  private async fetchEnabledDefinitionsForEntityIdsIfNeeded(entityIds: string[]) {
    const entityIdsToFetch = entityIds.filter(
      (entityId) =>
        !this.fetchedDefinitionsEntityIds.has(entityId) &&
        !this.fetchedEnabledDefinitionsEntityIds.has(entityId),
    )
    if (entityIdsToFetch.length === 0) {
      return
    }

    const { remoteDefinitions, successfullyFetchedEntityIds } =
      await this.fetchDefinitionsForEntityIdsWithDedup(
        entityIdsToFetch,
        this.pendingEnabledDefinitionsFetchesByEntityId,
        (ids) => this.fetchEnabledDefinitionsForEntityIds(ids),
      )

    runInAction(() => {
      successfullyFetchedEntityIds.forEach((entityId) => {
        this.fetchedEnabledDefinitionsEntityIds.add(entityId)
      })
    })

    remoteDefinitions.forEach((remoteDefinition) => {
      this.handleRemoteDefinitionUpdate(remoteDefinition)
    })
  }

  // Shared in-flight-request de-dup for the two "fetch definitions for entity
  // ids if needed" flows above: concurrent callers asking for an overlapping
  // set of not-yet-fetched ids reuse the same request instead of each
  // starting their own. Settles (rather than failing fast) so that one
  // sibling group's rejection doesn't discard another sibling group's
  // successfully fetched data, and so cleanup of `pendingFetchesByEntityId`
  // always runs - otherwise a rejected fetch would leave its entity ids
  // permanently stuck as "pending", blocking any retry.
  private async fetchDefinitionsForEntityIdsWithDedup(
    entityIdsToFetch: string[],
    pendingFetchesByEntityId: Map<string, Promise<RawWorkflowDefinition[]>>,
    startFetch: (entityIds: string[]) => Promise<RawWorkflowDefinition[]>,
  ): Promise<{
    remoteDefinitions: RawWorkflowDefinition[]
    successfullyFetchedEntityIds: string[]
  }> {
    const idsNeedingNewFetch = entityIdsToFetch.filter(
      (entityId) => !pendingFetchesByEntityId.has(entityId),
    )

    const newFetchPromise = idsNeedingNewFetch.length
      ? startFetch(idsNeedingNewFetch)
      : null

    if (newFetchPromise) {
      runInAction(() => {
        idsNeedingNewFetch.forEach((entityId) => {
          pendingFetchesByEntityId.set(entityId, newFetchPromise)
        })
      })
    }

    // Captured before the cleanup below removes entries, so we can still tell
    // which pending fetch (and therefore which settled result) each entity id
    // belongs to afterwards.
    const pendingFetchByEntityId = new Map(
      entityIdsToFetch.map(
        (entityId) => [entityId, pendingFetchesByEntityId.get(entityId)] as const,
      ),
    )

    const uniquePendingFetches = Array.from(
      new Set(Array.from(pendingFetchByEntityId.values()).filter(isNonNull)),
    )

    const settledFetches = await Promise.allSettled(uniquePendingFetches)
    const resultByPendingFetch = new Map(
      uniquePendingFetches.map((promise, index) => [promise, settledFetches[index]]),
    )

    if (newFetchPromise) {
      runInAction(() => {
        idsNeedingNewFetch.forEach((entityId) => {
          // Only clear entries that still point at this fetch - a newer,
          // still-pending fetch for the same id shouldn't be clobbered.
          if (pendingFetchesByEntityId.get(entityId) === newFetchPromise) {
            pendingFetchesByEntityId.delete(entityId)
          }
        })
      })
    }

    const remoteDefinitions = settledFetches
      .filter(
        (result): result is PromiseFulfilledResult<RawWorkflowDefinition[]> =>
          result.status === 'fulfilled',
      )
      .flatMap((result) => result.value)

    // Entity ids whose fetch actually succeeded - only these are safe for the
    // caller to mark as fetched or use for remote-deletion detection. An
    // entity id whose fetch rejected must stay unmarked so a later call
    // retries it.
    const successfullyFetchedEntityIds = entityIdsToFetch.filter((entityId) => {
      const pendingFetch = pendingFetchByEntityId.get(entityId)
      const result = pendingFetch && resultByPendingFetch.get(pendingFetch)

      if (result?.status === 'rejected') {
        this.log.warn('Failed to fetch workflow definitions for entity id', {
          entityId,
          error: result.reason,
        })
        return false
      }

      return true
    })

    return { remoteDefinitions, successfullyFetchedEntityIds }
  }

  private fetchDefinitionsForEntityIds(entityIds: string[]) {
    return this.fetchDefinitionsForEntityIdsPaginated(entityIds)
  }

  private fetchEnabledDefinitionsForEntityIds(entityIds: string[]) {
    return this.fetchDefinitionsForEntityIdsPaginated(entityIds, { enabled: true })
  }

  private async fetchDefinitionsForEntityIdsPaginated(
    entityIds: string[],
    { enabled }: { enabled?: boolean } = {},
  ) {
    const data: RawWorkflowDefinition[] = []
    let pageToken: string | undefined
    let pageCount = 0

    const invalidEntityIds = entityIds.filter((id) => !isValidPhoneNumberModelId(id))
    const validEntityIds = entityIds.filter((id) => isValidPhoneNumberModelId(id))

    if (invalidEntityIds.length > 0) {
      this.log.warn('Tried to fetch workflow definitions for invalid entity ids', {
        invalidEntityIds,
      })
    }

    if (validEntityIds.length === 0) {
      return []
    }

    do {
      const response = await this.root.transport.workflow.definitions.list({
        orgId: this.root.organization.getCurrentOrganization().id,
        userId: this.root.user.getCurrentUser().id,
        maxResults: 50,
        entityIds: validEntityIds,
        enabled,
        pageToken,
      })
      data.push(...response.data)
      pageToken = response.nextPageToken
      pageCount++

      if (pageToken && pageCount >= WorkflowStore.MAX_DEFINITIONS_FETCH_PAGES) {
        this.log.warn(
          'Stopped paginating workflow-definitions after hitting the page safety cap',
          { entityIdsCount: entityIds.length, pageCount, enabled },
        )
        break
      }
    } while (pageToken)

    return data
  }

  private updateOrCreateRingOrders(definition: WorkflowDefinitionModel) {
    return Promise.all(
      Object.values(definition.workflowSteps).map((step) => {
        if (step.definitionId !== 'WSDringUsers') {
          return Promise.resolve()
        }

        const ringOrderId = step.configuration?.find(
          (c) => c.variableKey === 'WVringOrderId',
        )?.value

        if (typeof ringOrderId !== 'string') {
          return Promise.resolve()
        }

        const ringOrder = this.root.ringOrder.getById(ringOrderId)

        if (!ringOrder) {
          return Promise.resolve()
        }

        return this.root.ringOrder.update(ringOrder)
      }),
    )
  }

  private handleRemoteDefinitionUpdate(definition: RawWorkflowDefinition) {
    const localDefinition = this.definitionsCollection.get(definition.id)

    // If there's no local definition, simply add this definition to the local collection
    if (!localDefinition) {
      this.definitionsCollection.put(new WorkflowDefinitionModel(definition))
      return
    }

    const updatedMetadata = workflowMetadataKeys.reduce(
      <K extends keyof WorkflowDefinitionMetadata>(
        acc: Partial<WorkflowDefinitionMetadata>,
        key: K,
      ) => {
        acc[key] = definition[key]
        return acc
      },
      {},
    )

    const hasMetadataChanges = Object.keys(updatedMetadata).length > 0

    const hasLocalChanges = this.definitionsBeforeModificationsById.has(definition.id)
    if (hasLocalChanges) {
      // If the local version has been modified but not published, instead of replacing it with
      // the remote version, we update the pre-modified version with the latest remote data.
      // At this point, the user can either:
      //  - discard (and be back to the latest remote data); or
      //  - publish (and replace the remote data with the modified one)
      this.definitionsBeforeModificationsById.set(definition.id, definition)

      // If metadata of the workflow has changed, then we need to update the local version
      // to have this new metadata, preserving any local step/config changes.
      if (hasMetadataChanges) {
        this.definitionsCollection.put(
          new WorkflowDefinitionModel({
            ...localDefinition.serialize(),
            ...updatedMetadata,
          }),
        )
      }
    } else {
      // if there's no local changes, we can simply replace the local definition with the remote one
      this.definitionsCollection.put(new WorkflowDefinitionModel(definition))
    }

    const definitionHistory = this.historyById.get(definition.id)
    if (definitionHistory && hasMetadataChanges) {
      // If there's workflow history and metadata of the workflow has changed, then we need to
      // update each history item to have this new metadata
      this.historyById.set(definition.id, {
        ...definitionHistory,
        history: definitionHistory.history.map((item) => ({
          ...item,
          ...updatedMetadata,
        })),
      })
    }
  }

  handleRemoteDefinitionDelete(definitionId: string) {
    this.definitionsCollection.delete(definitionId)
    this.historyById.delete(definitionId)
    this.definitionsBeforeModificationsById.delete(definitionId)
  }

  private getAllChildrenSteps(
    definitionId: string,
    workflowStep: WorkflowDefinitionModel['workflowSteps'][string],
  ) {
    const childrenSteps: WorkflowDefinitionModel['workflowSteps'][string][] = []

    workflowStep.branches?.forEach((branch) => {
      const nextStep = this.getWorkflowStepById(definitionId, branch.nextStepId)

      if (!nextStep) {
        return
      }

      childrenSteps.push(nextStep)

      if (nextStep.definitionId === 'WSDgoTo') {
        return
      }

      childrenSteps.push(...this.getAllChildrenSteps(definitionId, nextStep))
    })

    return childrenSteps
  }

  private deleteStepAndChildren(
    definitionId: string,
    workflowStep: WorkflowDefinitionModel['workflowSteps'][string],
    workflowSteps: WorkflowDefinitionModel['workflowSteps'],
  ) {
    this.getAllChildrenSteps(definitionId, workflowStep).forEach((childrenStep) => {
      delete workflowSteps[childrenStep.id]

      // Clean up branches pointing to deleted steps
      const stepsWithBranchesPointingToStep = Object.values(workflowSteps).filter(
        (step) => step.branches?.some((branch) => branch.nextStepId === childrenStep.id),
      )

      stepsWithBranchesPointingToStep.forEach((step) => {
        const stepToUpdate = workflowSteps[step.id]
        if (!stepToUpdate) {
          return
        }
        stepToUpdate.branches = stepToUpdate.branches?.filter(
          (branch) => branch.nextStepId !== childrenStep.id,
        )
      })
    })
  }

  private getSources(
    definitionId: string,
    stepId: string,
  ): WorkflowDefinitionModel['workflowSteps'][string][] {
    const definition = this.definitionsCollection.get(definitionId)

    if (!definition) {
      return []
    }

    return Object.values(definition.workflowSteps).filter((step) =>
      step.branches?.some((branch) => branch.nextStepId === stepId),
    )
  }

  getPreviousSteps(
    definitionId: string,
    startingStepId: string,
    includeGoToPaths: boolean,
  ): WorkflowDefinitionModel['workflowSteps'][string][] {
    const previousSteps: WorkflowDefinitionModel['workflowSteps'][string][] = []

    const populatePreviousSteps = (stepId: string) => {
      this.getSources(definitionId, stepId).forEach((s) => {
        // If the step is already in the list, that means we've encountered a loop and we can stop here
        if (previousSteps.some((step) => step.id === s.id)) {
          return
        }

        previousSteps.push(s)

        // If we're not including go-to paths and the step is a go-to step, we can stop here
        if (includeGoToPaths === false && s.definitionId === 'WSDgoTo') {
          return
        }

        populatePreviousSteps(s.id)
      })
    }

    populatePreviousSteps(startingStepId)

    return previousSteps
  }

  upsertDefinition(definition: RawWorkflowDefinition) {
    this.definitionsCollection.put(new WorkflowDefinitionModel(definition))
  }

  async createDefaultWorkflowDefinition(
    definition: Pick<
      CreateWorkflowDefinitionParams,
      'name' | 'emoji' | 'entityId' | 'orgId' | 'enabled'
    >,
  ) {
    const newVoicemailStep: WorkflowDefinitionModel['workflowSteps'][string] = {
      id: generateStepId(),
      definitionId: 'WSDvoicemail',
      branches: [],
      configuration: this.getVoicemailConfiguration({
        phoneNumberId: definition.entityId,
        type: 'duringHours',
      }),
    }

    const newRingUsersStep: WorkflowDefinitionModel['workflowSteps'][string] = {
      id: generateStepId(),
      definitionId: 'WSDringUsers',
      branches: [
        {
          key: 'WBcallMissed',
          nextStepId: newVoicemailStep.id,
        },
      ],
      configuration: this.getConfiguration(
        'WSDringUsers',
        definition.entityId,
        null,
        null,
      ),
    }

    const newDefinition = await this.createWorkflowDefinition({
      ...definition,
      internalTags: ['incoming-call-flow', 'incoming-call-flow-user-editable'],
      trigger: {
        definitionId: 'WTDincomingCall',
      },
      initialStepId: newRingUsersStep.id,
      workflowSteps: {
        [newRingUsersStep.id]: newRingUsersStep,
        [newVoicemailStep.id]: newVoicemailStep,
      },
    })

    await this.updateOrCreateRingOrders(new WorkflowDefinitionModel(newDefinition))
    this.upsertDefinition(newDefinition)

    return newDefinition
  }

  async createReceptionistWorkflowDefinition(
    definition: Pick<
      CreateWorkflowDefinitionParams,
      'name' | 'emoji' | 'entityId' | 'orgId' | 'enabled'
    >,
    duringHours: { triggerDefinitionId: string; greeting: string },
    afterHours: { triggerDefinitionId: string; greeting: string },
  ) {
    const duringHoursVoicemailStep = {
      id: generateStepId(),
      definitionId: 'WSDvoicemail',
      branches: [],
      configuration: this.getVoicemailConfiguration({
        phoneNumberId: definition.entityId,
        type: 'duringHours',
      }),
    } satisfies WorkflowDefinitionModel['workflowSteps'][string]

    const ringUsersStep = {
      id: generateStepId(),
      definitionId: 'WSDringUsers',
      branches: [{ key: 'WBcallMissed', nextStepId: duringHoursVoicemailStep.id }],
      configuration: this.getConfiguration(
        'WSDringUsers',
        definition.entityId,
        null,
        null,
      ),
    } satisfies WorkflowDefinitionModel['workflowSteps'][string]

    const duringHoursVoiceAgentStep = {
      id: generateStepId(),
      definitionId: 'WSDvoiceAgent',
      branches: [{ key: 'WBfallback', nextStepId: ringUsersStep.id }],
      configuration: [
        { variableKey: 'WVvoiceAgentGreeting', value: duringHours.greeting },
        {
          variableKey: 'WVagentTriggerDefinitionId',
          value: duringHours.triggerDefinitionId,
        },
      ],
    } satisfies WorkflowDefinitionModel['workflowSteps'][string]

    const afterHoursVoicemailStep = {
      id: generateStepId(),
      definitionId: 'WSDvoicemail',
      branches: [],
      configuration: this.getVoicemailConfiguration({
        phoneNumberId: definition.entityId,
        type: 'afterHours',
      }),
    } satisfies WorkflowDefinitionModel['workflowSteps'][string]

    const afterHoursVoiceAgentStep: WorkflowDefinitionModel['workflowSteps'][string] = {
      id: generateStepId(),
      definitionId: 'WSDvoiceAgent',
      branches: [{ key: 'WBfallback', nextStepId: afterHoursVoicemailStep.id }],
      configuration: [
        { variableKey: 'WVvoiceAgentGreeting', value: afterHours.greeting },
        {
          variableKey: 'WVagentTriggerDefinitionId',
          value: afterHours.triggerDefinitionId,
        },
      ],
    }

    const businessHoursStep: WorkflowDefinitionModel['workflowSteps'][string] = {
      id: generateStepId(),
      definitionId: 'WSDbusinessHours',
      branches: [
        { key: 'WBduringHours', nextStepId: duringHoursVoiceAgentStep.id },
        { key: 'WBafterHours', nextStepId: afterHoursVoiceAgentStep.id },
      ],
      configuration: [],
    }

    const newDefinition = await this.createWorkflowDefinition({
      ...definition,
      internalTags: ['incoming-call-flow', 'incoming-call-flow-user-editable'],
      trigger: { definitionId: 'WTDincomingCall' },
      initialStepId: businessHoursStep.id,
      workflowSteps: {
        [businessHoursStep.id]: businessHoursStep,
        [duringHoursVoiceAgentStep.id]: duringHoursVoiceAgentStep,
        [ringUsersStep.id]: ringUsersStep,
        [duringHoursVoicemailStep.id]: duringHoursVoicemailStep,
        [afterHoursVoiceAgentStep.id]: afterHoursVoiceAgentStep,
        [afterHoursVoicemailStep.id]: afterHoursVoicemailStep,
      },
    })

    await this.updateOrCreateRingOrders(new WorkflowDefinitionModel(newDefinition))
    this.upsertDefinition(newDefinition)

    return newDefinition
  }

  createWorkflowDefinition(definition: CreateWorkflowDefinitionParams) {
    return this.root.transport.workflow.definitions.post(definition)
  }

  updateWorkflowDefinition(definition: RawWorkflowDefinition) {
    return this.root.transport.workflow.definitions.put(definition.id, definition)
  }

  deleteWorkflowDefinition(definitionId: string) {
    const definition = this.definitionsCollection.get(definitionId)

    if (!definition) {
      return
    }

    return this.root.transport.workflow.definitions.delete(definitionId)
  }

  ringOrderIsUsedInALocalWorkflow(ringOrderId: string) {
    return Array.from(this.historyById.values()).some((history) =>
      history.history.some((definition) =>
        Object.values(definition.workflowSteps).some(
          (step) =>
            step.definitionId === 'WSDringUsers' &&
            step.configuration?.some(
              (c) => c.variableKey === 'WVringOrderId' && c.value === ringOrderId,
            ),
        ),
      ),
    )
  }

  private load() {
    return Promise.all([
      this.definitionsCollection.performQuery((repo) => repo.all()),
      this.triggerDefinitionsCollection.performQuery((repo) => repo.all()),
    ])
  }

  private subscribeToWebSocket() {
    return this.root.transport.onNotificationData.subscribe((data) => {
      switch (data.type) {
        case 'workflow-definition-create': {
          this.upsertDefinition(data.definition)
          break
        }
        case 'workflow-definition-update': {
          this.handleRemoteDefinitionUpdate(data.definition)
          break
        }
        case 'workflow-definition-delete': {
          this.handleRemoteDefinitionDelete(data.id)
          break
        }
      }
    })
  }
}
